import { createFileRoute } from "@tanstack/react-router";
import { zodValidator } from "@tanstack/zod-adapter";
import z from "zod";
import { ArticleDetails } from "@/components/article/article-details";
import { FullPageLoading } from "@/components/full-page-loading";
import { Button } from "@/components/ui/button";
import { ArticleEditorProvider } from "@/context/article-editor-context";
import {
  CollaborationProviderProvider,
  useCollaborationProviderSafe,
} from "@/context/collaboration-provider-context";
import { MediaPickerProvider } from "@/context/media-library-context";

export const Route = createFileRoute("/_auth-init-space/_authorized/stories/$articleId")({
  component: StoryDetailRoute,
  validateSearch: zodValidator(
    z.object({
      tab: z.enum(["content", "metadata"]).catch("content").default("content"),
      template: z.enum(["blank", "puzzle", "takeover"]).optional(),
      puzzleVariant: z.string().optional(),
      puzzleNumber: z.string().optional(),
      embedUrl: z.string().optional(),
      folderName: z.string().optional(),
    }),
  ),
});

function StoryDetailRoute() {
  const { articleId } = Route.useParams();

  return (
    <CollaborationProviderProvider articleId={articleId} key={articleId}>
      <LoadingGuard>
        <ArticleEditorProvider articleId={articleId}>
          <MediaPickerProvider>
            <ArticleDetails />
          </MediaPickerProvider>
        </ArticleEditorProvider>
      </LoadingGuard>
    </CollaborationProviderProvider>
  );
}

const PROBLEM_TEXT = {
  "not-found": "This story doesn't exist, or has been deleted.",
  unreachable: "This story can't be opened for editing right now. Check your connection and try again.",
  failed: "This story couldn't be opened for editing. Try again, or contact support if it keeps happening.",
} as const;

function LoadingGuard({ children }: { children: React.ReactNode }) {
  const { isReady, provider, problem, retry } = useCollaborationProviderSafe();

  if (problem && !isReady) {
    return (
      <div role="alert" className="h-dvh w-full flex flex-col items-center justify-center gap-4 font-inter">
        <p className="text-base text-center max-w-md">{PROBLEM_TEXT[problem]}</p>
        {problem !== "not-found" && <Button onClick={retry}>Retry</Button>}
      </div>
    );
  }

  if (!isReady || !provider) {
    return <FullPageLoading text="Loading story data..." />;
  }

  return children;
}
